import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { validateEmail, validateFio } from 'helpers/validators';
import { errorToast, successToast } from 'configuration/notifications';
import { priceTypesSelector, userTypesSelector } from 'store/auth/selectors';
import { AuthInput } from 'components/AuthInput/AuthInput';
import { userApi } from 'helpers/api/user';

import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { InputAutoComplete } from 'components/InputAutoComplete/InputAutoComplete';

const phoneMask = '+380 99 999 99 99';
const phonePlaceHolder = '+380 XX XXX XX XX';
const intervalSeconds = 15;

const RegisterForm = ({ pending, title, onSubmit }) => {
  const cityList = useSelector(priceTypesSelector);
  const positionList = useSelector(userTypesSelector);
  const [error, setError] = useState({});
  const [fio, setFIO] = useState('');
  const [email, setEmail] = useState('');
  const [city, setCity] = useState(null);
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [SMSShow, setSMSShow] = useState(false);
  const [isSmsConfirm, setIsSmsConfirm] = useState(false);

  const [counter, setCounter] = useState(intervalSeconds);
  const [counterShow, setCounterShow] = useState(false);
  const [position, setPosition] = useState('');
  const [password, setPassword] = useState('');
  const [repeatpassword, setRepeatPassword] = useState('');

  const handleSmsCode = (value) => setSmsCode(value);
  const handleFIO = (value) => setFIO(value);
  const handlePhone = (value) => setPhone(value);
  const handlePassword = (value) => setPassword(value);
  const handleRepeatPassword = (value) => setRepeatPassword(value);
  const handlePosition = (value) => setPosition(value);
  const handleEmail = (value) => setEmail(value);
  const handleCity = (value) => setCity(value);

  const isPhoneValid = () => phone && !phone.includes('X');
  const isSMSValid = () => smsCode && !smsCode.includes('X');

  useEffect(() => {
    const init = async () => {
      try {
        await userApi.codeCheck({ phone_number: phone, code: smsCode });
        successToast('Номер подтвержден');
        setIsSmsConfirm(true);
      } catch (error) {
        setIsSmsConfirm(false);
      }
      setSmsCode('');
      setSMSShow(false);
      return;
    };

    if (isSMSValid()) {
      init();
    }
  }, [smsCode]);

  const handleSendSms = async () => {
    await userApi.phoneCheck({ phone_number: phone });

    setSMSShow(true);
    setCounterShow(true);
    userApi.messageSend({ phone_number: phone });
    let timerId = setInterval(() => setCounter((counter) => counter - 1), 1000);
    setTimeout(() => {
      clearInterval(timerId);
      setCounter(intervalSeconds);
      setCounterShow(false);
    }, intervalSeconds * 1000);
  };

  const handleSubmit = () => {
    if (pending) {
      return;
    }
    if (!isSmsConfirm) {
      errorToast('Номер телефона не подтвержден!');
      return;
    }

    if (password !== repeatpassword) {
      setError({ ...error, repeatpassword: 'Пароли не совпадают' });
      errorToast('Пароли не совпадают!');
      return;
    }

    if (!position) {
      errorToast('Выберите должность!');
      return;
    }

    if (!city || !city.priceTypeID1C) {
      errorToast('Выберите город!');
      return;
    }

    const [isValidEmail, emessage] = validateEmail(email);
    if (!isValidEmail) {
      setError({ ...error, email: emessage });
      errorToast(emessage);
      return;
    }

    const [fioShort, fsmessage] = validateFio(fio);
    if (!fioShort) {
      setError({ ...error, fio: fsmessage });
      errorToast(fsmessage);
      return;
    }

    onSubmit({
      fio,
      fio_short: fsmessage,
      email,
      phone,
      priceTypeID: city.priceTypeID1C,
      position,
      password,
    });
  };

  return (
    <div className="login-block" style={{ maxWidth: '100%' }}>
      <div className="login-block-title">{title}</div>

      <div className="login-block-container">
        <form>
          <div className="login-group">
            <div className="login-column">
              <Input
                type="text"
                placeholder="ФИО"
                value={fio}
                onChange={handleFIO}
                required={true}
                errorText={error.fio || ''}
              />

              <InputAutoComplete
                icon="search"
                placeholder="Выберите город"
                className="input-form"
                selected={city ? { value: city.priceTypeID1C, text: city.typeName } : null}
                list={cityList.map((city) => ({
                  value: city.priceTypeID1C,
                  text: city.typeName,
                  returning: city,
                }))}
                size="none"
                onChange={handleCity}
              />
              <InputAutoComplete
                placeholder="Выберите должность"
                className="register-select"
                selected={position ? { value: position, text: position } : null}
                icon="search"
                size="none"
                list={positionList.map((type) => ({
                  value: type,
                  text: type,
                  returning: type,
                }))}
                onChange={handlePosition}
              />

              <Input
                type="password"
                placeholder="Пароль"
                value={password}
                onChange={handlePassword}
                required={true}
                errorText={error.password || ''}
              />
              <Input
                type="password"
                placeholder="Повторите пароль"
                value={repeatpassword}
                onChange={handleRepeatPassword}
                required={true}
                errorText={error.repeatpassword || ''}
              />
              <Input
                type="text"
                placeholder="Электронная почта"
                value={email}
                onChange={handleEmail}
                required={true}
                errorText={error.email || ''}
              />
              <div className="phone-number">
                <div className="phone-number-grid-element left">
                  <div>
                    <AuthInput
                      type="text"
                      placeholder={phonePlaceHolder}
                      mask={phoneMask}
                      value={phone}
                      onChange={handlePhone}
                      required={true}
                      errorText={error.email || ''}
                    />
                  </div>
                </div>
                <div className="phone-number-grid-element right">
                  <Button
                    onClick={handleSendSms}
                    className={cn(
                      'btn-phone',
                      (pending || !isPhoneValid() || counterShow || isSmsConfirm) && 'disabled-button'
                    )}
                    disabled={pending || !isPhoneValid() || counterShow || isSmsConfirm}
                    loader={pending}
                  >
                    Отправить код
                  </Button>
                </div>
              </div>
              {SMSShow && (
                <>
                  {counterShow && (
                    <span className="counterShow"> Отправить код повторно сможете через {counter} сек.</span>
                  )}
                  <AuthInput
                    mask="99 99"
                    placeholder="XX XX"
                    value={smsCode}
                    onChange={handleSmsCode}
                    required={true}
                  />
                </>
              )}
            </div>
          </div>

          <Button className="btn-form" onClick={handleSubmit} disabled={pending} loader={pending}>
            Зарегистрироваться
          </Button>
        </form>
      </div>
    </div>
  );
};

RegisterForm.propTypes = {
  pending: PropTypes.bool,
  title: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
};

RegisterForm.defaultProps = {
  pending: false,
  title: 'Авторизация',
};

export { RegisterForm };
